import React from 'react';
import { ScriptStep } from '../services/api';
import { X, Code, Info, Lightbulb, FileText } from 'lucide-react';
import './StepDetailsPanel.css';

interface StepDetailsPanelProps {
  step: ScriptStep | null;
  stepNumber: number;
  onClose: () => void;
}

export const StepDetailsPanel: React.FC<StepDetailsPanelProps> = ({ 
  step, 
  stepNumber,
  onClose 
}) => {
  if (!step) return null;

  return (
    <div className="step-details-panel">
      <div className="panel-header">
        <div className="panel-title">
          <span className="step-number-badge">{stepNumber}</span>
          <h3>{step.title}</h3>
        </div>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      <div className="panel-content">
        {/* Step Type */}
        <div className="detail-section">
          <div className="section-label">
            <Info size={14} />
            <span>Type</span>
          </div>
          <div className="type-badge">{step.type}</div>
        </div>

        {/* Description */}
        <div className="detail-section">
          <div className="section-label">
            <FileText size={14} />
            <span>Description</span>
          </div>
          <p className="section-text">{step.description}</p>
        </div>

        {/* What */}
        {step.details?.what && (
          <div className="detail-section">
            <div className="section-label">
              <Info size={14} />
              <span>What it does</span>
            </div>
            <p className="section-text">{step.details.what}</p>
          </div>
        )}

        {/* Why */}
        {step.details?.why && (
          <div className="detail-section">
            <div className="section-label">
              <Lightbulb size={14} />
              <span>Why it's important</span>
            </div>
            <p className="section-text">{step.details.why}</p>
          </div>
        )}

        {/* Example */}
        {step.details?.example && (
          <div className="detail-section">
            <div className="section-label">
              <Code size={14} />
              <span>Example</span>
            </div>
            <div className="example-box">
              <p className="section-text">{step.details.example}</p>
            </div>
          </div>
        )}

        {/* Code Lines */}
        {step.code_lines && (
          <div className="detail-section">
            <div className="section-label">
              <Code size={14} />
              <span>Code Reference</span>
            </div>
            <div className="code-reference">
              Lines {step.code_lines[0]} - {step.code_lines[1]}
            </div>
          </div>
        )}

        {/* Financial Details */}
        {step.financial_details && (
          <div className="detail-section">
            <div className="section-label">
              <Info size={14} />
              <span>Financial Details</span>
            </div>
            <div className="financial-details">
              {step.financial_details.calculations && (
                <div className="detail-item">
                  <strong>Calculations:</strong> {step.financial_details.calculations}
                </div>
              )}
              {step.financial_details.data_sources && (
                <div className="detail-item">
                  <strong>Data Sources:</strong> {step.financial_details.data_sources}
                </div>
              )}
              {step.financial_details.aggregations && (
                <div className="detail-item">
                  <strong>Aggregations:</strong> {step.financial_details.aggregations}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Validation Controls */}
        {step.validation_controls && (
          <div className="detail-section">
            <div className="section-label">
              <Info size={14} />
              <span>Validation Controls</span>
            </div>
            <p className="section-text">{step.validation_controls}</p>
          </div>
        )}

        {/* Error Handling */}
        {step.error_handling && (
          <div className="detail-section">
            <div className="section-label">
              <Info size={14} />
              <span>Error Handling</span>
            </div>
            <p className="section-text">{step.error_handling}</p>
          </div>
        )}
      </div>
    </div>
  );
};